/**
 * Cache Metrics Panel Component
 * Displays cache performance metrics (hit rate, hits/misses, latency)
 */

import { Card } from '@/components/ui/Card';
import { Database, TrendingUp, TrendingDown, Clock } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { CacheMetrics } from '@/types/monitoring';

export interface CacheMetricsPanelProps {
  metrics: CacheMetrics | null;
}

/**
 * Format percentage for display
 */
function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Format number with thousands separator
 */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * Format latency for display
 */
function formatLatency(latency: number): string {
  if (latency < 1) return '<1ms';
  return `${Math.round(latency)}ms`;
}

/**
 * Get color based on hit rate performance
 */
function getHitRateColor(hitRate: number): string {
  if (hitRate >= 0.8) return 'text-green-600';
  if (hitRate >= 0.5) return 'text-yellow-600';
  return 'text-red-600';
}

export function CacheMetricsPanel({ metrics }: CacheMetricsPanelProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.cache;

  if (!metrics) {
    return (
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t.title}</h3>
        </div>
        <div className="text-center py-8 text-gray-400">
          <Database className="w-12 h-12 mx-auto mb-2" />
          <p>{t.unavailable}</p>
        </div>
      </Card>
    );
  }

  const totalRequests = metrics.hits + metrics.misses;
  const hitRateColor = getHitRateColor(metrics.hit_rate);

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">{t.title}</h3>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Hit Rate */}
        <div className="bg-white rounded-lg p-4 border border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{t.hitRate}</span>
            <TrendingUp className={`w-4 h-4 ${hitRateColor}`} />
          </div>
          <div className={`text-2xl font-bold ${hitRateColor}`}>
            {formatPercentage(metrics.hit_rate)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {formatNumber(totalRequests)} {t.totalRequests}
          </div>
        </div>

        {/* Cache Hits */}
        <div className="bg-white rounded-lg p-4 border border-green-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{t.hits}</span>
            <TrendingUp className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-green-600">
            {formatNumber(metrics.hits)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {t.successfulRetrievals}
          </div>
        </div>

        {/* Cache Misses */}
        <div className="bg-white rounded-lg p-4 border border-orange-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{t.misses}</span>
            <TrendingDown className="w-4 h-4 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-orange-600">
            {formatNumber(metrics.misses)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {t.fetchFailures}
          </div>
        </div>

        {/* Average Latency */}
        <div className="bg-white rounded-lg p-4 border border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{t.avgLatency}</span>
            <Clock className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-purple-600">
            {formatLatency(metrics.avg_latency_ms)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {t.avgResponseTime}
          </div>
        </div>
      </div>
    </Card>
  );
}
